(() => {
  "use strict";

  const PROFILES = {
    "straight": { family: "straight", kind: "straight", cycles: 0.8, amp: 0.9, irregular: 0.18, x: 8, y: 19 },
    "kinky-straight": { family: "straight", kind: "crimp", cycles: 7, amp: 0.9, irregular: 0.7, x: 13, y: 28 },
    "body-wave": { family: "wave", kind: "wave", cycles: 1.65, amp: 6.8, irregular: 0.12, asym: 0.22, lean: 1.6, env: 0.12, x: 25, y: 34 },
    "loose-wave": { family: "wave", kind: "wave", cycles: 1.05, amp: 9.6, irregular: 0.2, asym: 0.06, lean: -2.2, env: 0.18, x: 33, y: 39 },
    "water-wave": { family: "wave", kind: "wave", cycles: 4.8, amp: 4.2, irregular: 0.55, asym: 0.34, lean: 0.8, env: 0.2, x: 46, y: 52 },
    "loose-deep-wave": { family: "wave", kind: "wave", cycles: 2.15, amp: 10.4, irregular: 0.28, asym: 0.1, lean: -1.5, env: 0.14, x: 53, y: 61 },
    "deep-wave": { family: "wave", kind: "wave", cycles: 2.95, amp: 12.0, irregular: 0.18, asym: 0.28, lean: 2.2, env: 0.08, x: 61, y: 70 },
    "italian-curl": { family: "curls", kind: "curl", loops: 3, radius: 10.2, chord: 14, irregular: 0.18, lean: 2.0, x: 68, y: 60 },
    "french-curl": { family: "curls", kind: "curl", loops: 4, radius: 9.0, chord: 12, irregular: 0.22, lean: -1.6, x: 74, y: 68 },
    "deep-curly": { family: "curls", kind: "curl", loops: 5, radius: 8.8, chord: 11.5, irregular: 0.25, lean: 1.2, x: 77, y: 78 },
    "pixie-curly": { family: "curls", kind: "curl", loops: 6, radius: 7.0, chord: 10.5, irregular: 0.35, lean: -2.0, x: 81, y: 73 },
    "burmese-curly": { family: "curls", kind: "curl", loops: 7, radius: 7.4, chord: 9.8, irregular: 0.22, lean: 0.7, x: 85, y: 85 },
    "jerry-curl": { family: "curls", kind: "curl", loops: 8, radius: 6.4, chord: 9.0, irregular: 0.15, lean: 1.8, x: 87, y: 89 },
    "kinky-curly": { family: "curls", kind: "coil", loops: 9, radius: 5.6, chord: 8.0, irregular: 0.32, lean: -1.2, x: 93, y: 93 },
    "afro-kinky-4c": { family: "curls", kind: "coil", loops: 10, radius: 4.8, chord: 7.2, irregular: 0.5, lean: 2.5, x: 98, y: 97 }
  };

  const FAMILY_SAMPLE = {
    straight: "straight",
    wave: "loose-wave",
    curls: "burmese-curly"
  };

  function hashSeed(value) {
    let h = 2166136261;
    for (let i = 0; i < value.length; i++) {
      h ^= value.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function jitter(profile, index, amount = 1) {
    const seed = hashSeed(profile + index);
    return (Math.sin(seed * 0.0001) + Math.sin(seed * 0.000017)) * 0.5 * amount;
  }

  function wavePoints(profile, id, x0, x1, cy, ampScale, phase, h) {
    const n = profile.kind === "crimp" ? 46 : 42;
    const points = [];
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const x = x0 + (x1 - x0) * t;
      let y = cy + (profile.lean || 0) * (t - 0.5);
      if (profile.kind === "straight") {
        y += Math.sin(t * Math.PI * 1.2 + phase) * profile.amp * 0.18;
      } else if (profile.kind === "crimp") {
        y += Math.sin(t * Math.PI * 2 * profile.cycles + phase) * profile.amp * 0.36;
        y += Math.sin(t * Math.PI * 2 * profile.cycles * 2.7 + phase * 1.7) * 0.45;
      } else {
        const envelope = 1 + (profile.env || 0) * Math.cos((t - 0.5) * Math.PI * 2 + phase * 0.35);
        y += Math.sin(t * Math.PI * 2 * profile.cycles + phase) * profile.amp * ampScale * envelope;
        y += Math.sin(t * Math.PI * 2 * profile.cycles * 2.05 + phase * 1.7) * profile.amp * ampScale * (profile.asym || 0.12);
        y += Math.sin(t * Math.PI * 2 * 11 + phase * 0.8) * profile.irregular;
      }
      points.push([x, y]);
    }
    return points;
  }

  function smoothPath(points) {
    let d = `M ${points[0][0].toFixed(2)} ${points[0][1].toFixed(2)}`;
    for (let i = 1; i < points.length; i++) {
      const p0 = points[i - 1];
      const p1 = points[i];
      const dx = p1[0] - p0[0];
      const dy = p1[1] - p0[1];
      const c1x = p0[0] + dx * 0.42;
      const c1y = p0[1] + dy * 0.08;
      const c2x = p1[0] - dx * 0.42;
      const c2y = p1[1] - dy * 0.08;
      d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p1[0].toFixed(2)} ${p1[1].toFixed(2)}`;
    }
    return d;
  }

  function curlPath(profile, id, x0, x1, cy, ampScale, phase) {
    const loops = Math.max(2, Math.round(profile.loops * ampScale));
    const chord = (x1 - x0) / loops;
    const radius = Math.min(chord * 0.72, (profile.radius || 6) * ampScale);
    let d = `M ${x0.toFixed(2)} ${cy.toFixed(2)}`;
    let up = true;
    for (let i = 0; i < loops; i++) {
      const endX = x0 + chord * (i + 1);
      const endY = cy + (profile.lean || 0) * ((i + 1) / loops - 0.5);
      // A major-arc loop that reads as a curl even at 50px.
      d += ` A ${radius.toFixed(2)} ${radius.toFixed(2)} 0 1 ${up ? 1 : 0} ${endX.toFixed(2)} ${endY.toFixed(2)}`;
      up = !up;
    }
    return d;
  }

  function strand(profileId, x0, x1, cy, ampScale = 1, phase = 0) {
    const profile = PROFILES[profileId];
    if (!profile) return "";
    if (profile.kind === "curl" || profile.kind === "coil") {
      return curlPath(profile, profileId, x0, x1, cy, ampScale, phase);
    }
    return smoothPath(wavePoints(profile, profileId, x0, x1, cy, ampScale, phase));
  }

  function signatureSVG(profileId, opts = {}) {
    const p = PROFILES[profileId];
    if (!p) return "";
    const w = opts.width || 80;
    const h = opts.height || 30;
    const pad = 4;
    const cy = h / 2;
    const ampScale = opts.ampScale || 1;
    const isCurl = p.kind === "curl" || p.kind === "coil";
    const spread = isCurl ? 1.7 : 3.6;
    const main = strand(profileId, pad, w - pad, cy, ampScale, 0.3);
    const layerA = strand(profileId, pad, w - pad, cy - spread * 0.45, ampScale * 0.72, 1.9);
    const layerB = strand(profileId, pad, w - pad, cy + spread, ampScale * 0.82, 3.4);
    const layerC = strand(profileId, pad, w - pad, cy - spread * 1.45, ampScale * 0.42, 4.9);
    const ghost = strand(profileId, pad, w - pad, cy + spread * 0.16, ampScale * 1.02, 5.6);
    const broken = strand(profileId, pad, w - pad, cy + spread * 0.62, ampScale * 0.55, 2.2);
    const flyaways = [0.11, 0.31, 0.51, 0.71, 0.91]
      .map((t, index) => {
        const fx = pad + (w - pad * 2) * t;
        const fy = cy + (index % 2 === 0 ? -spread * 0.9 : spread * 0.88);
        const dx = Math.max(3.2, w * 0.045);
        return `<path d="M ${fx.toFixed(2)} ${fy.toFixed(2)} C ${(fx + dx * 0.4).toFixed(2)} ${(fy - 3).toFixed(2)}, ${(fx + dx).toFixed(2)} ${(fy + 2.6).toFixed(2)}, ${(fx + dx * 1.65).toFixed(2)} ${(fy - 0.8).toFixed(2)}" fill="none" stroke="#73766c" stroke-width="0.5" stroke-linecap="round" opacity=".52"/>`;
      })
      .join("");
    const labels = opts.label ? `<text x="${pad}" y="${h - 1}" font-family="Arial, sans-serif" font-size="4.5" fill="#656760">${profileId}</text>` : "";
    const a11y = opts.decorative ? 'aria-hidden="true"' : `role="img" aria-label="${profileId.replaceAll("-", " ")} texture signature"`;
    return `<svg class="pattern-signature${opts.className ? " " + opts.className : ""}" viewBox="0 0 ${w} ${h}" ${a11y} preserveAspectRatio="xMidYMid meet">
      <path d="${ghost}" fill="none" stroke="#20221f" stroke-width="2.4" stroke-linecap="round" opacity=".04"/>
      <path d="${layerC}" fill="none" stroke="#9a9c91" stroke-width="0.46" stroke-linecap="round" opacity=".38"/>
      <path d="${layerB}" fill="none" stroke="#8b8d80" stroke-width="0.64" stroke-linecap="round" opacity=".46"/>
      <path d="${layerA}" fill="none" stroke="#656760" stroke-width="0.75" stroke-linecap="round" opacity=".64"/>
      <path d="${broken}" fill="none" stroke="#8b8d80" stroke-width="0.54" stroke-linecap="round" stroke-dasharray=".7 3.8" opacity=".44"/>
      <path d="${main}" fill="none" stroke="#20221f" stroke-width="1.26" stroke-linecap="round"/>
      ${flyaways}
      ${labels}
    </svg>`;
  }

  function familyPlateSVG(familyId, opts = {}) {
    const profileId = FAMILY_SAMPLE[familyId] || "body-wave";
    const w = opts.width || 200;
    const h = opts.height || 250;
    const rows = opts.rows || 7;
    const top = h * 0.12;
    const bottom = h * 0.88;
    const gap = (bottom - top) / (rows - 1);
    const ampScale = opts.ampScale || Math.max(0.8, Math.min(1.5, gap / 26));
    let paths = "";
    for (let i = 0; i < rows; i++) {
      const cy = top + i * gap;
      const phase = (i % 3) * 0.9;
      paths += `<path d="${strand(profileId, w * 0.08, w * 0.92, cy, ampScale, phase)}" fill="none" stroke="#20221f" stroke-width="${i === 2 ? 1.25 : 0.82}" stroke-linecap="round" opacity="${(0.32 + (i % 3) * 0.18).toFixed(2)}"/>`;
      if (i % 2 === 0) {
        paths += `<path d="${strand(profileId, w * 0.08, w * 0.92, cy + gap * 0.22, ampScale * 0.72, phase + 2.1)}" fill="none" stroke="#8b8d80" stroke-width="0.62" stroke-linecap="round" opacity=".3"/>`;
      }
    }
    const a11y = opts.decorative ? 'aria-hidden="true"' : `role="img" aria-label="${familyId} texture signature"`;
    return `<svg class="family-plate-svg" viewBox="0 0 ${w} ${h}" ${a11y} preserveAspectRatio="xMidYMid meet">${paths}</svg>`;
  }

  function largeSignatureSVG(profileId, opts = {}) {
    const w = opts.width || 520;
    const h = opts.height || 360;
    const rows = opts.rows || 7;
    const top = h * 0.14;
    const bottom = h * 0.86;
    const gap = (bottom - top) / (rows - 1);
    let paths = "";
    for (let i = 0; i < rows; i++) {
      const cy = top + i * gap;
      const phase = (i % 4) * 0.8;
      paths += `<path d="${strand(profileId, w * 0.06, w * 0.94, cy, 1.4, phase)}" fill="none" stroke="#20221f" stroke-width="${i === 3 ? 1.7 : 1.0}" stroke-linecap="round" opacity="${(0.3 + (i % 3) * 0.2).toFixed(2)}"/>`;
    }
    const a11y = opts.decorative ? 'aria-hidden="true"' : `role="img" aria-label="${profileId.replaceAll("-", " ")} pattern signature"`;
    return `<svg class="pattern-large-svg" viewBox="0 0 ${w} ${h}" ${a11y} preserveAspectRatio="xMidYMid meet">${paths}</svg>`;
  }

  window.TF_TEXTURE_MENU_PATTERNS = {
    profiles: PROFILES,
    sample: FAMILY_SAMPLE,
    signatureSVG,
    familyPlateSVG,
    largeSignatureSVG
  };
})();

(() => {
  if (customElements.get('tf-texture-menu')) return;
  let instance = 0;
  class TextureMenu extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      const uid = `tf-texture-${++instance}`;
      const tabs = [...this.querySelectorAll('[data-texture-format]')];
      const panels = [...this.querySelectorAll('[data-texture-panel]')];
      const tablist = this.querySelector('[data-texture-tabs]');
      const preview = this.querySelector('.tftm__preview');
      const details = this.closest('details');
      this.hidePreview = () => { preview.hidden = true; };
      tablist.setAttribute('role', 'tablist');
      tablist.setAttribute('aria-label', this.querySelector('h2').textContent);
      tabs.forEach((tab, index) => {
        const panel = panels[index];
        tab.id = `${uid}-tab-${index}`;
        panel.id = `${uid}-panel-${index}`;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        const select = () => {
          this.hidePreview();
          tabs.forEach((other, i) => {
            const active = i === index;
            other.setAttribute('aria-selected', String(active));
            other.setAttribute('aria-pressed', String(active));
            other.tabIndex = active ? 0 : -1;
            panels[i].hidden = !active;
          });
          panel.querySelector('.tftm__scroll').scrollTop = 0;
        };
        tab.setAttribute('aria-selected', String(index === 0));
        tab.tabIndex = index === 0 ? 0 : -1;
        tab.addEventListener('click', select, { signal });
        tab.addEventListener('keydown', event => {
          let target;
          if (event.key === 'ArrowRight') target = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') target = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = tabs.length - 1;
          if (target === undefined) return;
          event.preventDefault();
          tabs[target].click();
          tabs[target].focus();
        }, { signal });
        const jumps = panel.querySelector('.tftm__jumps');
        jumps.replaceChildren();
        panel.querySelectorAll('[data-texture-family]').forEach((group, groupIndex) => {
          group.id = `${uid}-group-${index}-${groupIndex}`;
          group.querySelector('h3').id = `${group.id}-title`;
          group.setAttribute('aria-labelledby', `${group.id}-title`);
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = `${group.dataset.textureLabel} · ${group.dataset.textureCount}`;
          button.setAttribute('aria-controls', group.id);
          button.addEventListener('click', () => {
            this.hidePreview();
            const scroll = panel.querySelector('.tftm__scroll');
            scroll.scrollTop += group.getBoundingClientRect().top - scroll.getBoundingClientRect().top - 12;
          }, { signal });
          jumps.append(button);
        });
        jumps.hidden = !jumps.children.length;
      });
      const patterns = window.TF_TEXTURE_MENU_PATTERNS;
      this.querySelectorAll('[data-texture-card]').forEach(card => {
        const signature = card.querySelector('[data-texture-signature]');
        signature.innerHTML = patterns.signatureSVG(card.dataset.textureKey, { width: 120, height: 36, decorative: true });
        signature.hidden = !signature.firstElementChild;
        const show = () => {
          if (this.dataset.hover === 'false' || !matchMedia('(min-width:990px) and (hover:hover)').matches || !card.dataset.textureImage) return;
          const img = preview.querySelector('img');
          img.src = card.dataset.textureImage;
          img.alt = card.dataset.textureName;
          const caption = preview.querySelector('figcaption');
          caption.textContent = card.dataset.textureName;
          if (this.dataset.curves !== 'false') {
            const line = document.createElement('div');
            line.className = 'tftm__signature';
            line.innerHTML = signature.innerHTML;
            caption.append(line);
          }
          preview.hidden = false;
          const r = card.getBoundingClientRect();
          const width = preview.offsetWidth, height = preview.offsetHeight;
          let left = r.right + 14;
          if (left + width > innerWidth - 12) left = r.left - width - 14;
          const top = Math.max(12, Math.min(r.top + (r.height - height) / 2, innerHeight - height - 12));
          preview.style.left = `${Math.max(12, Math.min(left, innerWidth - width - 12))}px`;
          preview.style.top = `${top}px`;
        };
        card.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') show(); }, { signal });
        card.addEventListener('pointerleave', this.hidePreview, { signal });
        card.addEventListener('focus', show, { signal });
        card.addEventListener('blur', this.hidePreview, { signal });
      });
      this.querySelectorAll('[data-texture-touch]').forEach(button => button.addEventListener('click', () => {
        const item = button.closest('.tftm__item');
        const full = item.dataset.full !== 'true';
        item.dataset.full = String(full);
        button.setAttribute('aria-expanded', String(full));
      }, { signal }));
      this.querySelector('[data-texture-close]').addEventListener('click', () => {
        this.hidePreview();
        details.open = false;
        details.querySelector('summary').focus();
      }, { signal });
      details?.addEventListener('toggle', () => { if (!details.open) this.hidePreview(); }, { signal });
      this.addEventListener('keydown', event => { if (event.key === 'Escape') { this.hidePreview(); if (details) details.open = false; } }, { signal });
      this.addEventListener('scroll', this.hidePreview, { capture: true, signal });
      window.addEventListener('resize', this.hidePreview, { signal });
      window.addEventListener('scroll', this.hidePreview, { signal, capture: true });
      document.addEventListener('shopify:section:unload', this.hidePreview, { signal });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.ready = false;
    }
  }
  customElements.define('tf-texture-menu', TextureMenu);
})();
